import { Drawer } from '@/components'
import { AnalysisTypeSelect } from '@/components/common/Form/AnalysisTypeSelect/AnalysisTypeSelect'
import { BasisSelect } from '@/components/common/Form/BasisSelect/BasisSelect'
import { ComparedToSelect } from '@/components/common/Form/ComparedToSelect/ComparedToSelect'
import { shouldDisplayComparedToSetting } from '@/components/common/Form/ComparedToSelect/shouldDisplayComparedToSetting'
import { BenchmarkingAreaSetting } from '@/features/Analytics/components/BenchmarkinAreaSetting/BenchmarkingAreaSetting'
import { shouldDisplayAreaSetting } from '@/features/Analytics/components/BenchmarkinAreaSetting/shouldDisplayAreaSetting'
import { BenchmarkingDisplaySetting } from '@/features/Analytics/components/BenchmarkingDisplaySetting/BenchmarkingDisplaySetting'
import { shouldDisplayWeatherSetting } from '@/features/Analytics/components/WeatherSetting/shouldDisplayWeatherSetting'
import { WeatherSetting } from '@/features/Analytics/components/WeatherSetting/WeatherSetting'
import {
    BenchmarkingAnalysis,
    BenchmarkingBasis,
    BenchmarkingDisplay,
    BenchmarkingReportSettings,
    BenchmarkingRequestParams,
    ComparisonPeriod,
} from '@/hooks'
import { DEFAULT_BENCHMARKING_REPORT_SETTINGS } from '@/utils/constants'
import { Timeline } from '@/utils/date'
import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { ResetReportSettingsButton } from '../../molecules/ResetReportSettingsButton/ResetReportSettingsButton'

type BencharkingDrawerProps = {
    reportSettings: BenchmarkingReportSettings
    setReportSettings: (settings: BenchmarkingReportSettings) => void
    baselineYearTimeline?: Timeline
    previousYearTimeline?: Timeline
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
    toggleButtonRef?: React.Ref<HTMLButtonElement>
    filters: BenchmarkingRequestParams
}

export const BenchmarkingDrawer: React.FC<BencharkingDrawerProps> = (props) => {
    const {
        reportSettings,
        setReportSettings,
        baselineYearTimeline,
        previousYearTimeline,
        isOpen,
        setIsOpen,
        toggleButtonRef,
        filters,
    } = props
    const { t } = useTranslation('translations')
    const hasDefaultSettings =
        JSON.stringify(reportSettings) ===
        JSON.stringify(DEFAULT_BENCHMARKING_REPORT_SETTINGS)

    const onChangeReportSettings = (
        settings: Partial<BenchmarkingReportSettings>
    ) => {
        setReportSettings({
            ...reportSettings,
            ...settings,
        })
    }

    return (
        <Drawer
            drawerToggleLabel={t('analytics.common.report_settings_title')}
            drawerToggleIconName="tune"
            title={t('analytics.common.report_settings_title')}
            headerContent={
                <ResetReportSettingsButton
                    onClick={() => {
                        setReportSettings(DEFAULT_BENCHMARKING_REPORT_SETTINGS)
                    }}
                    isHidden={hasDefaultSettings}
                    data-testid={'reset-settings-button'}
                />
            }
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            toggleButtonRef={toggleButtonRef}
        >
            <div className="space-y-7">
                <div className="space-y-3" data-testid="report-setup-settings">
                    <div className="text-heading-caps">
                        {t('analytics.benchmarking.report_setup.title')}
                    </div>
                    <BenchmarkingDisplaySetting
                        filters={filters}
                        reportSettings={reportSettings}
                        value={reportSettings.display}
                        onChange={(display: BenchmarkingDisplay) => {
                            onChangeReportSettings({ display })
                        }}
                    />
                    <BasisSelect
                        reportSettings={reportSettings}
                        value={reportSettings.basis}
                        onChange={(basis: BenchmarkingBasis) => {
                            onChangeReportSettings({ basis })
                        }}
                    />
                    <AnalysisTypeSelect
                        value={reportSettings.analysisType}
                        onChange={(analysisType: BenchmarkingAnalysis) => {
                            onChangeReportSettings({ analysisType })
                        }}
                    />
                    {shouldDisplayComparedToSetting(reportSettings) && (
                        <ComparedToSelect
                            value={
                                reportSettings.comparisonPeriod ??
                                'previousYear'
                            }
                            onChange={(comparisonPeriod: ComparisonPeriod) => {
                                onChangeReportSettings({ comparisonPeriod })
                            }}
                            baselineYearTimeline={baselineYearTimeline}
                            previousYearTimeline={previousYearTimeline}
                        />
                    )}
                </div>
                <div className="space-y-3" data-testid="normalization-settings">
                    {(shouldDisplayWeatherSetting(reportSettings) ||
                        shouldDisplayAreaSetting(reportSettings)) && (
                        <div className="text-heading-caps">
                            {t('analytics.benchmarking.normalization.title')}
                        </div>
                    )}
                    {shouldDisplayWeatherSetting(reportSettings) && (
                        <WeatherSetting
                            value={reportSettings.applyWeatherNormalization}
                            onChange={(applyWeatherNormalization: boolean) => {
                                onChangeReportSettings({
                                    applyWeatherNormalization,
                                })
                            }}
                        />
                    )}
                    {shouldDisplayAreaSetting(reportSettings) && (
                        <BenchmarkingAreaSetting
                            value={reportSettings.applySquareFootage}
                            onChange={(applySquareFootage: boolean) => {
                                onChangeReportSettings({ applySquareFootage })
                            }}
                        />
                    )}
                </div>
            </div>
        </Drawer>
    )
}
